import React, { useEffect, useRef, useState } from 'react';
import { 
    Box, 
    Typography,
    Button, 
    Container,
    Stack, 
    CircularProgress
} from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import SubHeaderView from './form/sub-header-view';
import TheoryView from './form/theory-view';
import ObjectiveView from './form/objective-view';
import FileUploadView from './form/file-upload-view';
import ResourceDialog from './resource-dialog';
import BackdropLoader from '../../../../components/backdrop-loader';
import { AssessmentPreviewModel, Item } from './preview-model';
import TimerView from './timer-view';
import { PreStartAssessment } from './pre-start-assessment';
import { uploadResource } from '../../../../utils/upload-resource';
import axios from 'axios';
import SubmittedDialog from './submitted-dialog';
import sanitizeString from '../../../../utils/sanitize-name';
import { useSnackbar } from '../../../../components/snackbar';

function shuffleArray<T>(array: T[]): T[] {
  const newArr = [...array];
  for (let i = newArr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArr[i], newArr[j]] = [newArr[j], newArr[i]];
  }
  return newArr;
}


type RouteParams = {
    classroom_id: string;
    assessment_id: string;
};

const API_URL = process.env.REACT_APP_API_URL;


const AssessmentPreview: React.FC = () => {
    const { classroom_id, assessment_id } = useParams<RouteParams>();
    const [assessment, setAssessment] = useState<AssessmentPreviewModel | null>(null);

    const [currentSection, setCurrentSection] = useState(0);

    const [uploadPercent, setUploadPercent] = useState<number | null>(null);

    const itemRefs = useRef<Record<string, () => Item>>({});

    const [resource, setResource] = useState<string>('');

    const [started, setStarted] = useState(false);

    const [submitting, setSubmitting] = useState(false);
    const [submittedDialog, setSubmitedDialog] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const autoSaveTimeout = useRef<NodeJS.Timeout | null>(null);

    const navigate = useNavigate();

    const { showMessage } = useSnackbar();


    const handlePrevious = () => {
        setCurrentSection(currentSection - 1);
    };

    const handleNext = () => {
        setCurrentSection(currentSection + 1);
    };

    const debouncedAutoSave = () => {
        if (!assessment) return;

        if (autoSaveTimeout.current) clearTimeout(autoSaveTimeout.current);

        autoSaveTimeout.current = setTimeout(() => {
            
            const updatedQuestions: Item[][] = [];

            for (let sectionKey = 0; sectionKey < assessment.questions.length; sectionKey++) {
                let sectionQuestions = [];
                
                for (const item of assessment.questions[sectionKey]) {
                    if (itemRefs.current[item.id]) {
                        sectionQuestions.push(itemRefs.current[item.id]())
                    }
                    else {
                        sectionQuestions.push(item)
                    }
                }
                
                updatedQuestions.push(sectionQuestions);
            }

            const newAssessment = {
                ...assessment,
                questions: updatedQuestions
            };

            setAssessment(newAssessment);    
        }, 300);
    };
        

    const handleSubmit = async () => {
        if (!assessment || !assessment.questions) return;
        
        try {
            setSubmitting(true);
            const answers: any[][] = [];

            for (let curSection = 0; curSection < assessment.questions.length; curSection++) {
                const sectionAnswers = [];

                for (const question of assessment.questions[curSection]) {
                    if (question.type === 0) continue;

                    if (question.type === 5) {
                        if (question.answer) {
                            const path = `classrooms/${classroom_id}/assessments/${assessment_id}/submissions/${sanitizeString(question.answer.name)}`;                   
                        
                            await uploadResource(path, question.answer, question.answer.type, (percent) => {
                                setUploadPercent(percent);
                            });
                            
                            setUploadPercent(null);

                            sectionAnswers.push({ id: question.id, answer: path })
                        } else {
                            sectionAnswers.push({ id: question.id, answer: "" })
                        }
                        
                    } else {
                        sectionAnswers.push({ id: question.id, answer: question.answer })
                    }
                }

                answers.push(sectionAnswers);
            }

            await axios.put(
                `${API_URL}/instructor/classrooms/${classroom_id}/assessments/${assessment_id}/submit`,
                { "answer": answers },
                {  withCredentials: true }
            );
            setSubmitted(true);
            setSubmitedDialog(true);
      
        } catch (error) {
            handleApiError(error);
        } finally {
            setSubmitting(false);
        }
    };



    const handleApiError = (error: any) => {
        if (error?.response?.status === 401) {
            navigate("/sign-in");
            return;
        }

        const message =
            error?.response?.data?.detail ||
            error?.response?.data?.message ||
            error.message ||
            "An unexpected error occurred";

        showMessage(message, 0);
    };

    useEffect(() => {
        const storedAssessment = sessionStorage.getItem('previewAssessment');
        if (!storedAssessment) return;

        try {
            const parsedAssessment: AssessmentPreviewModel = JSON.parse(storedAssessment);

            if (!parsedAssessment.questions) {
                setAssessment(parsedAssessment); // still set it, even if no questions
                return;
            }

            const clonedAssessment: AssessmentPreviewModel = {
                ...parsedAssessment,
                questions: parsedAssessment.questions.map((section) => {
                    if (!parsedAssessment.shuffle_questions) {
                        return section.map((question) => {
                            if (question.shuffle_options && question.options?.length) {
                                return {
                                    ...question,
                                    options: shuffleArray([...question.options]),
                                };
                            }
                            return question;
                        });
                    }

                // shuffle questions (excluding type 0)
                const staticItems: { index: number; item: Item }[] = [];
                const shuffleItems: Item[] = [];

                section.forEach((item, index) => {
                if (item.type === 0) {
                    staticItems.push({ index, item });
                } else {
                    shuffleItems.push(item);
                }
                });

                const shuffled = shuffleArray(shuffleItems);

                const result: Item[] = [];
                let shuffleIndex = 0;

                for (let i = 0; i < section.length; i++) {
                const staticItem = staticItems.find((s) => s.index === i);
                if (staticItem) {
                    result.push(staticItem.item);
                } else {
                    result.push(shuffled[shuffleIndex++]);
                }
                }

                // shuffle options within questions
                return result.map((q) => {
                if (q.shuffle_options && q.options?.length) {
                    return {
                    ...q,
                    options: shuffleArray([...q.options]),
                    };
                }
                return q;
                });
            }),
            };

            setAssessment(clonedAssessment);
        } catch (error) {
            console.error("Invalid assessment in sessionStorage", error);
        }

        return () => {
            sessionStorage.removeItem('previewAssessment');
        };
        }, [assessment_id]);


    if (!assessment) {
        return <BackdropLoader/>
    }
        
    if (!started) {
        return (
            <PreStartAssessment assessment={assessment} setStarted={setStarted}/>
        );
    };
    
    return (
        <Box sx={{ backgroundColor: "#c6e1f5", minHeight: "100vh" }}>
            <Box
                sx={{
                    position: "fixed",
                    width: '100%',
                    backgroundColor: "#1e293b",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
            >
                <Typography
                    sx={{
                        padding: 2,
                        color: "white",
                        fontWeight: 600,
                        fontSize: "1rem",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "70%",
                    }}
                >
                    {assessment.title}
                </Typography>
    
                {assessment.duration > 0 && (
                    <TimerView 
                        assessment={assessment} 
                        onTimeExpired={handleSubmit} 
                        started={started}
                    />
                )}
            </Box>
    
            <Container sx={{ paddingTop: 12, paddingBottom: 4 }}>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Section {currentSection + 1} of {Object.keys(assessment.questions).length}
                    </Typography>
                    <Box
                        sx={{
                            width: "100%",
                            height: 4,
                            backgroundColor: "#e2e8f0",
                            borderRadius: 2,
                            overflow: "hidden",
                        }}
                    >
                        <Box
                            sx={{
                                width: `${((currentSection + 1) / Object.keys(assessment.questions).length) * 100}%`,
                                height: "100%",
                                backgroundColor: "#3b82f6",
                                transition: "width 0.3s ease",
                            }}
                        />
                    </Box>
                </Box>
    
                <Box sx={{ mb: 4 }}>
                    {assessment.questions &&
                        (() => {
                            const sectionQuestions = assessment.questions[currentSection] || [];

                            return (
                                <Stack key={currentSection} spacing={2}>
                                    {sectionQuestions.map((question, index) => {
                                        const baseProps = {
                                            item: question,
                                            register: (id: string, getItem: () => Item) => {
                                                itemRefs.current[id] = getItem;
                                                debouncedAutoSave();
                                            },
                                            setResource: setResource
                                        };

                                        return (
                                            <React.Fragment key={question.id || index}>
                                                {question.type === 0 && (
                                                    <SubHeaderView setResource={setResource} item={question} />
                                                )}
                                                {(question.type === 1 || question.type === 2) && (
                                                    <TheoryView {...baseProps} />
                                                )}
                                                {(question.type === 3 || question.type === 4) && (
                                                    <ObjectiveView {...baseProps} />
                                                )}
                                                {question.type === 5 && (
                                                    <FileUploadView {...baseProps} />
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </Stack>
                            );
                        })()
                    }
                </Box>

    
     
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 2,
                        flexWrap: { xs: "wrap", sm: "nowrap" },
                    }}
                >
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        <Button
                            variant="outlined"
                            onClick={handlePrevious}
                            disabled={currentSection === 0}
                            sx={{
                                textTransform: "none",
                                fontWeight: 500,
                                borderColor: "#616263",
                                color: "#374151",
                                '&:hover': {
                                    borderColor: "#9ca3af",
                                    backgroundColor: "#f9fafb",
                                },
                            }}
                        >
                            <ArrowBack sx={{ mr: 1 }} />
                            Prev
                        </Button>
    
                        <Button
                            variant="outlined"
                            onClick={handleNext}
                            disabled={currentSection === Object.keys(assessment.questions).length - 1}
                            sx={{
                                textTransform: "none",
                                fontWeight: 500,
                                borderColor: "#616263",
                                color: "#374151",
                                '&:hover': {
                                    borderColor: "#9ca3af",
                                    backgroundColor: "#f9fafb",
                                },
                            }}
                        >
                            Next
                            <ArrowForward sx={{ ml: 1 }} />
                        </Button>
                    </Box>
    
                    {(currentSection === Object.keys(assessment.questions).length - 1) && (
                        <Button
                            onClick={handleSubmit}
                            variant="contained"
                            disabled={submitting || submitted}
                            sx={{
                                backgroundColor: '#059669',
                                textTransform: "none",
                                fontWeight: 600,
                                px: 3,
                                py: 1,
                                '&:hover': {
                                    backgroundColor: '#047857',
                                },
                                '&:disabled': {
                                    backgroundColor: '#9ca3af',
                                }
                            }}
                        >
                            {submitting ? <CircularProgress color="inherit" size={20}/> : "Submit"}
                        </Button>
                    )}
                </Box>
            </Container>
                    
            <ResourceDialog resource={resource} setResource={setResource}/>


            <SubmittedDialog
                open={submittedDialog}
                onClose={() => setSubmitedDialog(false)}
            />

            { submitting && <BackdropLoader progress={uploadPercent}/>}

        </Box>
    );
};

export default AssessmentPreview;
